import { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { resizeImage } from "@/components/Avatar";
import { ClipboardCheck, Camera, X, Receipt, Clock, Wallet, CheckCircle2, CreditCard } from "lucide-react";

const t = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const mins = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));

export function VisitSummaryCard({ request, isSeeker, onChanged }) {
  const s = request.visit_summary;
  const [showPay, setShowPay] = useState(false);
  const [dispute, setDispute] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!s) return null;
  const r = s.reimbursement || { status: "pending" };

  const pay = async () => {
    setBusy(true);
    try { await api.post(`/requests/${request.id}/reimburse`); toast.success(`Reimbursed ₹${s.amount_spent.toFixed(2)} to ${s.helper_name}`); setShowPay(false); onChanged?.(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendDispute = async () => {
    setBusy(true);
    try { await api.post(`/requests/${request.id}/dispute-expense`, { note }); toast.success("We've flagged this for the Kindred team."); setDispute(false); onChanged?.(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="visit-summary-card" className="mt-8 pt-6 border-t border-[#E2D9CE]">
      <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2 mb-3"><ClipboardCheck className="w-5 h-5 text-[#2E7D6B]" /> Visit summary</h3>
      <div className="bg-[#E8F4F1] border-2 border-[#B5DBD1] rounded-2xl p-5">
        <div className="flex items-center gap-3">
          <Avatar user={{ name: s.helper_name, avatar: s.helper_avatar }} size="sm" />
          <div>
            <p className="font-semibold text-slate-900">{s.helper_name}</p>
            {request.arrived_at && request.completed_at && (
              <p className="text-sm text-slate-700 flex items-center gap-1"><Clock className="w-4 h-4" /> {t(request.arrived_at)} – {t(request.completed_at)} · {mins(request.arrived_at, request.completed_at)} min</p>
            )}
          </div>
        </div>
        {s.note && <p data-testid="summary-note" className="mt-4 text-lg text-slate-800">{s.note}</p>}
        {s.amount_spent > 0 && (
          <div className="mt-3">
            <p data-testid="summary-amount" className="font-semibold text-slate-900 flex items-center gap-2"><Wallet className="w-5 h-5 text-[#D96B43]" /> Money spent: ₹{s.amount_spent.toFixed(2)}
              {r.status === "paid" && <span data-testid="reimbursed-badge" className="ml-2 inline-flex items-center gap-1 text-[#1F5F4F] bg-white px-2 py-0.5 rounded-full text-sm"><CheckCircle2 className="w-4 h-4" /> Reimbursed</span>}
              {r.status === "disputed" && <span data-testid="disputed-badge" className="ml-2 text-red-700 bg-white px-2 py-0.5 rounded-full text-sm">Under review</span>}
              {r.status === "waived" && <span className="ml-2 text-slate-600 bg-white px-2 py-0.5 rounded-full text-sm">Waived</span>}
            </p>
            {isSeeker && r.status === "pending" && (
              <div className="flex flex-wrap gap-3 mt-3">
                <button data-testid="reimburse-btn" onClick={() => setShowPay(true)} className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-5 min-h-[48px] rounded-xl flex items-center gap-2"><CreditCard className="w-5 h-5" /> Reimburse ₹{s.amount_spent.toFixed(2)}</button>
                <button data-testid="dispute-btn" onClick={() => setDispute(true)} className="border-2 border-[#E2D9CE] hover:border-red-400 text-slate-700 font-semibold px-5 min-h-[48px] rounded-xl">Something's not right</button>
              </div>
            )}
            {dispute && (
              <div data-testid="dispute-form" className="mt-3 bg-white rounded-xl p-4 border border-[#E2D9CE]">
                <label className="block font-semibold text-slate-800 mb-2">Tell us briefly what's wrong</label>
                <textarea data-testid="dispute-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-[#E2D9CE] focus:border-[#D96B43] outline-none" />
                <div className="flex gap-3 mt-3">
                  <button data-testid="dispute-submit-btn" onClick={sendDispute} disabled={busy} className="bg-slate-800 text-white font-semibold px-5 min-h-[44px] rounded-xl disabled:opacity-60">Send to Kindred team</button>
                  <button onClick={() => setDispute(false)} className="text-slate-600 font-semibold px-3 min-h-[44px]">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
        {showPay && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowPay(false)}>
            <div data-testid="reimburse-modal" className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-3">Reimburse {s.helper_name}</h3>
              <p className="text-slate-600 mb-4">You're paying back <span className="font-semibold text-slate-900">₹{s.amount_spent.toFixed(2)}</span> spent on your behalf for “{request.title}”.</p>
              <div className="bg-[#FFF7E6] text-[#A9741A] text-sm rounded-xl p-3 mb-4">This is a simulated payment for the demo. No card is charged.</div>
              <button data-testid="confirm-reimburse-btn" onClick={pay} disabled={busy} className="w-full bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl">{busy ? "Processing…" : `Pay ₹${s.amount_spent.toFixed(2)}`}</button>
            </div>
          </div>
        )}
        {s.receipts?.length > 0 && (
          <div className="mt-4">
            <p className="font-semibold text-slate-800 flex items-center gap-2 mb-2"><Receipt className="w-5 h-5 text-[#D96B43]" /> Receipts</p>
            <div className="flex flex-wrap gap-3">{s.receipts.map((r, i) => <a key={r.slice(-40) + i} href={r} target="_blank" rel="noreferrer"><img data-testid={`summary-receipt-${i}`} src={r} alt={`Receipt ${i + 1}`} className="h-28 rounded-xl border border-[#B5DBD1] object-cover" /></a>)}</div>
          </div>
        )}
        <p className="text-sm text-slate-600 mt-4">Emailed to the seeker{s.emails?.family === "sent" ? " and family contact" : ""}.</p>
      </div>
    </section>
  );
}

export default function VisitWrapUp({ requestId, onDone, onSkip, asModal = false }) {
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [busy, setBusy] = useState(false);

  const addReceipt = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - receipts.length);
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error("Receipts must be photos (JPG/PNG)."); continue; }
      try { setReceipts((r) => [...r, ...(r.length < 3 ? [null] : [])].filter(Boolean)); const img = await resizeImage(f, 900); setReceipts((r) => [...r, img].slice(0, 3)); }
      catch { toast.error("Couldn't read that photo."); }
    }
    e.target.value = "";
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/requests/${requestId}/summary`, { note, amount_spent: parseFloat(amount || 0), receipts });
      toast.success("Summary sent to the seeker and family. Thank you!");
      onDone?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const body = (
    <div data-testid="visit-wrapup" className={`bg-white rounded-3xl p-6 sm:p-8 ${asModal ? "max-w-lg w-full shadow-2xl border-4 border-[#2E7D6B]" : "border-2 border-[#B5DBD1] mt-8"}`}>
      <h3 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-[#2E7D6B]" /> Wrap up the visit</h3>
      <p className="text-slate-600 mt-1">A short summary reassures the seeker and their family. Everything is optional.</p>
      <label className="block font-semibold text-slate-800 mt-5 mb-2">What we did</label>
      <textarea data-testid="wrapup-note" rows={3} maxLength={800} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Picked up medicines and groceries, had a cup of tea together."
        className="w-full px-4 py-3 rounded-xl border-2 border-[#E2D9CE] focus:border-[#2E7D6B] outline-none text-lg" />
      <label className="block font-semibold text-slate-800 mt-4 mb-2">Money spent on their behalf (₹)</label>
      <input data-testid="wrapup-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0"
        className="w-full px-4 min-h-[52px] rounded-xl border-2 border-[#E2D9CE] focus:border-[#2E7D6B] outline-none text-lg" />
      <label className="block font-semibold text-slate-800 mt-4 mb-2">Receipt photos (up to 3)</label>
      <div className="flex flex-wrap gap-3">
        {receipts.map((r, i) => (
          <div key={r.slice(-40) + i} className="relative"><img src={r} alt={`Receipt ${i + 1}`} className="h-24 w-24 object-cover rounded-xl border border-[#E2D9CE]" />
            <button type="button" data-testid={`wrapup-remove-receipt-${i}`} onClick={() => setReceipts(receipts.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-white border border-[#E2D9CE] rounded-full p-1"><X className="w-4 h-4" /></button></div>
        ))}
        {receipts.length < 3 && (
          <label className="h-24 w-24 rounded-xl border-2 border-dashed border-[#E2D9CE] hover:border-[#2E7D6B] flex flex-col items-center justify-center cursor-pointer text-slate-600 text-sm">
            <Camera className="w-6 h-6 text-[#D96B43]" /> Add<input data-testid="wrapup-receipt-input" type="file" accept="image/*" multiple className="hidden" onChange={addReceipt} />
          </label>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button data-testid="wrapup-submit-btn" onClick={submit} disabled={busy} className="flex-1 bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-bold min-h-[56px] rounded-xl text-lg">{busy ? "Sending…" : "Send summary"}</button>
        {onSkip && <button data-testid="wrapup-skip-btn" onClick={onSkip} disabled={busy} className="flex-1 border-2 border-[#E2D9CE] hover:border-slate-400 text-slate-700 font-semibold min-h-[56px] rounded-xl">Skip for now</button>}
      </div>
    </div>
  );
  if (!asModal) return body;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">{body}</div>;
}
